// IssueLogsGrid.tsx
import { Button, Card, Divider, Group, Pagination, Select, Stack, Text } from "@mantine/core";
import React, { useMemo } from "react";

import { DatePickerInput, DatesRangeValue } from "@mantine/dates";

import type { ColDef, GridOptions } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

import { skipToken } from "@reduxjs/toolkit/query";
import { IIssueLogs } from "@schemas/issueSchema";
import { useGetProjectByProjectIdIssueLogsQuery } from "../../../../../openapi";
import { FailedAlert } from "../../../../components";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { ISSUE_COLUMN_LABELS } from "../issueConstants";
import {
  setIssueLogsCreatedAtRangeInRedux,
  setIssueLogsCurrentPageInRedux,
  setIssueLogsPageSizeInRedux,
  thunkOpenIssueModalById,
} from "../issueSlice";
import { toIsoRangeOrNull } from "./issueLogHelper";
import {
  selectIssueLogsBaseArgs,
  selectIssueLogsCreatedAtRange,
  selectIssueLogsCurrentPage,
  selectIssueLogsFormatted,
  selectIssueLogsPageSize,
} from "./issueLogSelectors";

type IIssueLogRow = IIssueLogs;

const formatDateTime = (val: unknown) => {
  if (!val) return "";
  const d = val instanceof Date ? val : new Date(String(val));
  if (Number.isNaN(d.getTime())) return String(val);
  return d.toLocaleString();
};

const PAGE_SIZE_OPTIONS = ["50", "100", "200", "500"];

export const IssueLogsGrid: React.FC = () => {
  const dispatch = useAppDispatch();

  const range = useAppSelector(selectIssueLogsCreatedAtRange);
  const pageSize = useAppSelector(selectIssueLogsPageSize);
  const currentPage = useAppSelector(selectIssueLogsCurrentPage);

  const createdAtRange: DatesRangeValue = [range[0] ? new Date(range[0]) : null, range[1] ? new Date(range[1]) : null];

  const args = useAppSelector(selectIssueLogsBaseArgs);

  const { isError } = useGetProjectByProjectIdIssueLogsQuery(args ?? skipToken);

  const formattedData = useAppSelector(selectIssueLogsFormatted);

  const totalCount = formattedData?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / Math.max(1, pageSize)));

  const columnDefs = useMemo<ColDef<IIssueLogRow>[]>(
    () => [
      {
        headerName: "Opprettet",
        field: "createdAt",
        minWidth: 210,
        valueFormatter: (p) => formatDateTime(p.value),
      },
      { headerName: "Bruker", field: "userName", minWidth: 160 },
      { headerName: "Type", field: "taskOrIssue", minWidth: 110 },
      { headerName: "Kolonne", field: "column", minWidth: 160, valueFormatter: (p) => ISSUE_COLUMN_LABELS[p.value] ?? p.value },
      { headerName: "Gammel verdi", field: "oldValue", flex: 1, minWidth: 220 },
      { headerName: "Ny verdi", field: "newValue", flex: 1, minWidth: 220 },
      {
        headerName: "Åpne",
        colId: "open",
        field: "issueId",
        minWidth: 120,
        maxWidth: 140,
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => {
          const content = params.data as IIssueLogRow | undefined;
          if (!content?.issueId) return null;

          return (
            <Button
              size="xs"
              variant="light"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                dispatch(thunkOpenIssueModalById(content.issueId as any));
              }}
            >
              Åpne
            </Button>
          );
        },
      },
    ],
    [],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      sortable: false,
      filter: false,
    }),
    [],
  );

  const gridOptions = useMemo<GridOptions<IIssueLogRow>>(
    () => ({
      rowModelType: "clientSide",
      animateRows: false,
      suppressRowClickSelection: true,
    }),
    [],
  );

  return (
    <Card withBorder radius="md" p="md" style={{ height: "100vh" }}>
      <Stack gap="sm" h="100%">
        <Group justify="space-between" align="flex-end">
          <Stack gap={4}>
            <DatePickerInput
              type="range"
              label="Opprettet (intervall)"
              placeholder="Velg datointervall"
              value={createdAtRange}
              onChange={(dates) => {
                const [createdAtFromIso, createdAtToIso] = toIsoRangeOrNull(dates);

                // ✅ Filterendring => hopp tilbake til side 1
                dispatch(setIssueLogsCreatedAtRangeInRedux([createdAtFromIso, createdAtToIso]));
                dispatch(setIssueLogsCurrentPageInRedux(1));
              }}
              clearable
            />
          </Stack>

          <Group gap="sm" align="flex-end">
            <Select
              label="Rader per side"
              size="xs"
              data={PAGE_SIZE_OPTIONS}
              value={String(pageSize)}
              onChange={(v) => {
                const next = Number(v ?? "200");
                dispatch(setIssueLogsPageSizeInRedux(next));
                dispatch(setIssueLogsCurrentPageInRedux(1)); // ✅ reset page ved pageSize-endring
              }}
              w={140}
            />
          </Group>
        </Group>

        <Divider />

        {isError && <FailedAlert />}

        {/* Grid */}
        <div className="ag-theme-quartz" style={{ flex: 1, width: "100%" }}>
          <AgGridReact<IIssueLogRow>
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            gridOptions={gridOptions}
            rowData={formattedData?.items ?? []}
            getRowId={(p) =>
              String((p.data as any)?._id ?? `${(p.data as any)?.issueId}-${(p.data as any)?.createdAt}`)
            }
          />
        </div>

        {/* Footer: paging */}
        <Group justify="space-between" align="center">
          <Text size="sm" c="dimmed">
            Viser {formattedData?.items?.length ?? 0} av {totalCount} rader
          </Text>

          <Pagination
            value={Math.min(currentPage, totalPages)}
            onChange={(page) => dispatch(setIssueLogsCurrentPageInRedux(page))}
            total={totalPages}
            siblings={1}
            boundaries={1}
          />
        </Group>
      </Stack>
    </Card>
  );
};
